import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBoard, boardStore } from "@/lib/boardStore";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CategoryDialog({ open, onOpenChange }) {
  const { categories, cards } = useBoard();
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#8B5CF6");

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Give the category a name");
      return;
    }
    boardStore.addCategory(name, newColor);
    setNewName("");
    setNewColor("#8B5CF6");
    toast("Category added");
  };

  const handleDelete = (id) => {
    if (categories.length <= 1) {
      toast.error("Keep at least one category");
      return;
    }
    const usedBy = cards.filter((c) => c.categoryId === id).length;
    const msg = usedBy
      ? `${usedBy} card${usedBy === 1 ? " is" : "s are"} in this category. They'll be reassigned. Continue?`
      : "Delete this category?";
    if (!window.confirm(msg)) return;
    boardStore.removeCategory(id);
    toast("Category removed");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="category-dialog"
        className="bg-[#0f0b07] border border-[#5c4426]/40 text-[#f4ebd8] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="noir-title text-2xl text-[#f4d9a8]">
            Manage Categories
          </DialogTitle>
          <DialogDescription className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285]/70">
            Assign a color to each category — it stains the text on every card
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-y-auto thin-scroll pr-1">
          {categories.map((cat) => (
            <div
              key={cat.id}
              data-testid={`category-row-${cat.id}`}
              className="flex items-center gap-2 bg-[#1a140c] border border-[#5c4426]/40 rounded px-3 py-2"
            >
              <input
                type="color"
                value={cat.color}
                onChange={(e) =>
                  boardStore.updateCategory(cat.id, { color: e.target.value })
                }
                data-testid={`category-color-${cat.id}`}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent p-0"
              />
              <Input
                value={cat.name}
                onChange={(e) =>
                  boardStore.updateCategory(cat.id, { name: e.target.value })
                }
                data-testid={`category-name-${cat.id}`}
                className="flex-1 bg-[#0f0b07] border-[#5c4426]/50 text-[#f4ebd8] typewriter"
              />
              <button
                onClick={() => handleDelete(cat.id)}
                data-testid={`delete-category-${cat.id}`}
                className="text-[#c9b285] hover:text-[#e08a8a] p-1"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-[#5c4426]/30 pt-3 space-y-2">
          <div className="typewriter text-[10px] uppercase tracking-widest text-[#c9b285]">
            Add category
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              data-testid="new-category-color"
              className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent p-0"
            />
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="e.g. Suspects, Motives, Alibis…"
              data-testid="new-category-name"
              className="flex-1 bg-[#1a140c] border-[#5c4426]/50 text-[#f4ebd8] typewriter"
              maxLength={30}
            />
            <Button
              onClick={handleAdd}
              data-testid="add-category-btn"
              className="bg-[#c9702a] hover:bg-[#e08432] text-[#1a140c] typewriter"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
